import {
  isTerminalArrivalStopType,
  type MLTripSummary,
  type StopSummary,
} from "../types";

import { buildClusterDisplayNameById } from "./metadataLookup";

export function getNonTerminalStopSummaries(
  trip: MLTripSummary,
): StopSummary[] {
  return trip.stopSummaries.filter(
    (stop) => !isTerminalArrivalStopType(stop.stopType),
  );
}

export function hasRecordedStopArrival(stop: StopSummary): boolean {
  return stop.arrivalTime != null && stop.arrivalTime !== "";
}

/** Last completed delivery stop by `sequencePosition` (excludes terminal return). */
export function getLastNonTerminalStopBySequence(
  trip: MLTripSummary,
): StopSummary | null {
  return getNonTerminalStopSummaries(trip).reduce<StopSummary | null>(
    (latestStop, stop) => {
      if (!hasRecordedStopArrival(stop)) {
        return latestStop;
      }

      if (latestStop == null) {
        return stop;
      }

      if (stop.sequencePosition > latestStop.sequencePosition) {
        return stop;
      }

      return latestStop;
    },
    null,
  );
}

export function getLastDeliveryTimestamp(trip: MLTripSummary): string | null {
  const lastStop = getLastNonTerminalStopBySequence(trip);
  return lastStop?.arrivalTime ?? null;
}

export function getStopClusterDisplayName(
  clusterName: string | undefined,
  clusterId: string,
): string {
  if (clusterName != null && clusterName !== "") {
    return clusterName;
  }
  return clusterId;
}

export type LatestArrivalDisplay = {
  placeName: string | undefined;
  arrivalTime: string | undefined;
};

export function getLatestArrivalDisplay(
  trip: MLTripSummary,
  allTripsForClusterLabels: readonly MLTripSummary[] = [],
): LatestArrivalDisplay {
  const lastStop = getLastNonTerminalStopBySequence(trip);
  if (lastStop == null) {
    return { placeName: undefined, arrivalTime: undefined };
  }

  const clusterDisplayNameById = buildClusterDisplayNameById([
    trip,
    ...allTripsForClusterLabels,
  ]);

  return {
    placeName:
      clusterDisplayNameById.get(lastStop.clusterId) ??
      getStopClusterDisplayName(lastStop.clusterName, lastStop.clusterId),
    arrivalTime: lastStop.arrivalTime,
  };
}
